import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Typography,
    Box
} from '@mui/material';
import { StaffService } from '../api/services/staffService';

const formatDate = (dateInput) => {
    if (!dateInput) return '';
    if (typeof dateInput === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
            return dateInput;
        }
        if (dateInput.includes('T')) {
            return dateInput.split('T')[0];
        }
        const d = new Date(dateInput);
        if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return dateInput;
    }
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
        const year = dateInput.getFullYear();
        const month = String(dateInput.getMonth() + 1).padStart(2, '0');
        const day = String(dateInput.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    return dateInput;
};

export default function EditAssignmentModal({ open, onClose, onSuccess, shiftList = [], staffList = [], assignmentData }) {
    const [formData, setFormData] = useState({
        shiftId: '',
        date: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open && assignmentData) {
            setFormData({
                shiftId: assignmentData.shiftId || assignmentData.shift?.id || assignmentData.shift?._id || '',
                date: formatDate(assignmentData.date || assignmentData.startDate)
            });
            setError('');
            setIsConfirmOpen(false);
        }
    }, [open, assignmentData]);

    const getDeptId = (item) => {
        if (!item) return '';
        return item.departmentId || item.department?.id || item.department?._id || item.department_id || '';
    };

    const getDeptName = (item) => {
        if (!item) return '';
        if (typeof item.department === 'string') return item.department;
        return item.department?.name || item.departmentName || '';
    };

    const checkDepartmentMatch = (staff, shift) => {
        if (!staff || !shift) return { matches: true };

        const shiftDeptId = getDeptId(shift);
        const shiftDeptName = getDeptName(shift);
        
        if (!shiftDeptId && (!shiftDeptName || shiftDeptName.toLowerCase() === 'all' || shiftDeptName.toLowerCase() === 'all departments')) {
            return { matches: true };
        }

        const staffDeptId = getDeptId(staff);
        const staffDeptName = getDeptName(staff);

        if (shiftDeptId && staffDeptId && String(shiftDeptId) === String(staffDeptId)) {
            return { matches: true };
        }

        if (shiftDeptName && staffDeptName && shiftDeptName.trim().toLowerCase() === staffDeptName.trim().toLowerCase()) {
            return { matches: true };
        }

        const staffLabel = staff.fullName || staff.name || 'Staff member';
        const staffDeptDisplay = staffDeptName || 'Unassigned';
        const shiftDeptDisplay = shiftDeptName || 'another department';

        return {
            matches: false,
            message: `Department mismatch: "${staffLabel}" is in "${staffDeptDisplay}", but shift "${shift.name}" is designated for "${shiftDeptDisplay}".`
        };
    };

    const staffFromList = (staffList || []).find(s => (s.id || s._id) === (assignmentData?.staffId || assignmentData?.staff?.id || assignmentData?.staff?._id));
    const staffObj = assignmentData?.staff || staffFromList || assignmentData;
    const staffName = assignmentData?.fullName || assignmentData?.name || assignmentData?.staff?.fullName || assignmentData?.staff?.name || staffFromList?.fullName || staffFromList?.name || 'Staff Member';
    const staffDept = getDeptName(staffObj) || getDeptName(staffFromList);

    const handleShiftChange = (newShiftId) => {
        setFormData(prev => ({ ...prev, shiftId: newShiftId }));
        const selectedShift = shiftList.find(sh => (sh.id || sh._id) === newShiftId);
        const match = checkDepartmentMatch(staffObj, selectedShift);
        if (!match.matches) {
            setError(match.message);
        } else {
            setError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            if (!formData.shiftId || !formData.date) {
                throw new Error('Shift and Date are required.');
            }

            const selectedShift = shiftList.find(sh => (sh.id || sh._id) === formData.shiftId);
            const match = checkDepartmentMatch(staffObj, selectedShift);
            if (!match.matches) {
                throw new Error(match.message);
            }

            const staffId = assignmentData?.staffId || assignmentData?.staff?.id || assignmentData?.staff?._id || staffObj?.id || staffObj?._id;
            if (!staffId) {
                throw new Error('Staff ID could not be identified for this assignment.');
            }

            const payload = {
                staffId: staffId,
                shiftId: formData.shiftId,
                date: formatDate(formData.date)
            };

            const assignmentId = assignmentData.id || assignmentData._id;
            await StaffService.updateAssignment(assignmentId, payload);
            
            onSuccess('Assignment updated successfully');
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to update assignment.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        setError('');
        try {
            const assignmentId = assignmentData.id || assignmentData._id;
            await StaffService.deleteAssignment(assignmentId);
            onSuccess('Shift assignment removed');
            setIsConfirmOpen(false);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to remove shift assignment.');
            setIsConfirmOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Shift Assignment</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        
                        <Grid container spacing={2.5}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Staff Member (Read-Only)
                                </Typography>
                                <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {staffName} {staffDept ? `(${staffDept})` : ''}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth size="small" required>
                                    <InputLabel>Shift</InputLabel>
                                    <Select
                                        value={formData.shiftId}
                                        onChange={(e) => handleShiftChange(e.target.value)}
                                        label="Shift"
                                    >
                                        {shiftList.map(shift => {
                                            const shiftDept = getDeptName(shift);
                                            const match = checkDepartmentMatch(staffObj, shift);
                                            const isMismatch = !match.matches;

                                            return (
                                                <MenuItem 
                                                    key={shift.id || shift._id} 
                                                    value={shift.id || shift._id}
                                                    disabled={isMismatch}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                        <span>{shift.name} ({shift.startTime} - {shift.endTime})</span>
                                                        {shiftDept && (
                                                            <Typography variant="caption" color={isMismatch ? "error" : "text.secondary"} sx={{ ml: 1, fontStyle: 'italic' }}>
                                                                {shiftDept}{isMismatch ? ' (Incompatible)' : ''}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    label="Date"
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                    fullWidth
                                    required
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50', justifyContent: 'space-between' }}>
                        <Button 
                            color="error" 
                            onClick={() => setIsConfirmOpen(true)}
                            disabled={isSubmitting || isDeleting}
                        >
                            Remove
                        </Button>
                        <Box>
                            <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>Cancel</Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting || isDeleting}
                                startIcon={isSubmitting && <CircularProgress size={16} />}
                                sx={{ px: 4 }}
                            >
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={isConfirmOpen}
                onClose={() => !isDeleting && setIsConfirmOpen(false)}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Remove Shift Assignment</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove this shift assignment for <strong>{staffName}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
                    <Button 
                        onClick={() => setIsConfirmOpen(false)} 
                        color="inherit" 
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDelete} 
                        color="error" 
                        variant="contained"
                        disabled={isDeleting}
                        startIcon={isDeleting && <CircularProgress size={16} color="inherit" />}
                    >
                        {isDeleting ? 'Removing...' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
